import React, { useState, useMemo, useRef } from 'react';
import { PhonicsActivity } from '@/api/types';
import { findSoundsInString } from '@/utils/sounds';
import { useUi, useAssetCdn } from '@/context/Ui';
import { CdnObjectType, SymbolType, SUFFIXES } from '@/types';
import { AudioPlayer } from '@/components';
import { Activity, ActivityStep } from '../Activity';
import { ActivityTypeProps } from '../types';
import { WhitePanel } from '../components/WhitePanel';
import { ActivityTitleStep } from '../components/ActivityTitleStep';
import { Toolbar } from '../components/Toolbar';
import {
  GrabGameSuffixes,
  GrabGameSuffixesSuffixEngine,
} from '../components/GrabGameSuffixes';

const getSuffixEngine = (
  activity: PhonicsActivity,
): GrabGameSuffixesSuffixEngine => {
  if (activity.words2 && activity.words2.length > 0) {
    return 'correctAnswers';
  }

  return 'pluralize';
};

interface AddSuffixesStepProps extends ActivityTypeProps {
  index: number;
  total: number;
  word: string;
}

const AddSuffixesStep = ({
  activity,
  mode,
  wrapperClassName,
  isPreview,
  index,
  total,
  word,
}: AddSuffixesStepProps) => {
  if (!activity.sounds) {
    throw new Error('Sounds are required for this activity');
  }

  const playerRef = useRef<AudioPlayer>(null);

  const { sendAlert } = useUi();

  const [playerReady, setPlayerReady] = useState<boolean>(false);

  const [hasPlayedAudioOnce, setHasPlayedAudioOnce] = useState<boolean>(false);

  const mediaUrl = useAssetCdn(word, SymbolType.Word, CdnObjectType.Audio);

  const sounds = useMemo(
    () => activity?.sounds && findSoundsInString(SUFFIXES, activity.sounds),
    [activity],
  );

  return (
    <ActivityStep
      key={`${activity.id}-${index}-1`}
      stepIdentifier={`${activity.id}-${index}-1`}
      activity={activity}
      mode={mode}
      stepNumber={index + 1}
      totalSteps={total}
      wrapperClassName={wrapperClassName}
      isPreview={isPreview}
      usePanel
      showTitle={true}
      showActivityPagination={false}
    >
      <Toolbar
        onPlay={() => {
          playerRef.current?.play();

          setHasPlayedAudioOnce(true);
        }}
        playDisabled={!playerReady}
        hintPlay={!hasPlayedAudioOnce}
      />

      <WhitePanel mode="basic" kind="fill" color="paper">
        {sounds && (
          <GrabGameSuffixes
            word={word}
            suffixes={sounds}
            correctAnswers={activity.words2 || undefined}
            suffixEngine={getSuffixEngine(activity)}
          />
        )}
      </WhitePanel>

      <AudioPlayer
        ref={playerRef}
        src={mediaUrl || ''}
        onReadySrc={() => setPlayerReady(true)}
        onLoadTimeoutSrc={() =>
          sendAlert('We could not load some of the audio for this lesson.', {
            type: 'warning',
            title: 'There was a problem',
          })
        }
      />
    </ActivityStep>
  );
};

export const ReadingAddSuffixes = ({
  activity,
  mode,
  wrapperClassName,
  isPreview,
}: ActivityTypeProps) => {
  if (!activity.words) {
    throw new Error('Words are required for this activity');
  }

  const { words } = activity;

  return (
    <Activity
      steps={[
        <ActivityTitleStep
          key={`${activity.id}-title`}
          activity={activity}
          mode={mode}
          wrapperClassName={wrapperClassName}
          isPreview={isPreview}
          index={0}
          total={words.length + 1}
        />,
        ...words.flatMap((word, index) => [
          <AddSuffixesStep
            key={`${activity.id}-${index}-1`}
            activity={activity}
            mode={mode}
            wrapperClassName={wrapperClassName}
            isPreview={isPreview}
            index={index + 1}
            total={words.length + 1}
            word={word}
          />,
        ]),
      ]}
    />
  );
};
